import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { TodayTaskList } from "../components/TodayTaskList";
import { TeamTodayStrip } from "../components/TeamTodayStrip";
import { CallOutcomeSheet } from "../components/CallOutcomeSheet";
import { CallConfirmSheet } from "../components/CallConfirmSheet";
import { BookingSheet } from "../components/BookingSheet";
import { OutcomeSheet } from "../components/OutcomeSheet";
import { RescheduleSheet } from "../components/RescheduleSheet";
import { VenueSheet } from "../components/VenueSheet";
import { primaryContactAction } from "../lib/contactAction";

export default function TodayPage({ session }) {
  const commercial = session.user.role === "commerciale";
  const [data, setData] = useState(null);
  const [call, setCall] = useState(null);
  const [skipped, setSkipped] = useState([]);
  const [callOutcome, setCallOutcome] = useState(null);
  const [callIntent, setCallIntent] = useState(null);
  const [booking, setBooking] = useState(null);
  const [outcomeId, setOutcomeId] = useState(null);
  const [venueId, setVenueId] = useState(null);
  const [revision, setRevision] = useState(0);
  const [rescheduleId, setRescheduleId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const navigate = useNavigate();
  const reload = useCallback(() => { setRevision((n) => n + 1);
    window.dispatchEvent(new Event("luchador-tasks-updated")); }, []);
  useEffect(() => { const onVisible = () => { if (document.visibilityState === "visible") reload(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [reload]);
  useEffect(() => {
    let live = true;
    api.get("/today").then(({ data: response }) => { if (live) { setData(response); setError(""); } })
      .catch((e) => { if (live) setError(errorMessage(e)); });
    if (commercial) api.get("/calls/next", { params: { skipped: skipped.join(",") } })
      .then(({ data: result }) => { if (live) setCall(result.venue); })
      .catch((e) => { if (live) setError(errorMessage(e)); });
    return () => { live = false; };
  }, [commercial, revision, skipped]);
  const skip = () => { if (call) setSkipped((current) => [...current, call.id]); };
  const startBooking = (venueIdToBook, callNote, callId) => {
    setCallOutcome(null); setOutcomeId(null);
    window.setTimeout(() => setBooking({ venueId: venueIdToBook, callNote, callId }), 180);
  };
  const startVisit = async (venue) => {
    try {
      const { data: record } = await api.post(`/venues/${venue.id}/visits/start`, {},
        { headers: { "X-CSRF-Token": session.csrf_token } });
      setCallOutcome({ venue, callId: record.id }); reload();
    } catch (e) { setError(errorMessage(e)); }
  };
  const openTask = async (task) => {
    if (task.kind === "meeting_outcome") { setOutcomeId(task.reference_id); return; }
    if (task.kind === "reschedule") { setRescheduleId(task.reference_id); return; }
    try {
      const { data: venue } = await api.get(`/venues/${task.venue_id}`);
      if (task.kind === "call_outcome") { setCallOutcome({ venue, callId: task.reference_id }); return; }
      if (["book_first", "book_second"].includes(task.kind)) {
        const previous = venue.appointments?.slice(-1)[0];
        setBooking({ venueId: venue.id, participantIds: task.kind === "book_second"
          ? previous?.participant_ids || [previous?.presenter_id].filter(Boolean) : [] });
        return;
      }
      if (["callback", "first_call", "enrollment_reminder"].includes(task.kind)) {
        const action = primaryContactAction(venue);
        if (action) { setCallIntent({ venue, action }); return; }
      }
      setVenueId(venue.id);
    } catch (e) { setError(errorMessage(e)); }
  };
  return <div className="today-page" data-testid="page-oggi">
    <span className="section-label" data-testid="today-label">OGGI</span>
    <h1 className="page-heading" data-testid="today-heading">Da fare<span className="today-heading-count" data-testid="today-due-count">{data?.task_count || 0}</span></h1>
    {error && <p className="form-error" role="alert" data-testid="today-error">{error}</p>}
    {notice && <p className="venue-success" role="status" data-testid="today-notice">{notice}</p>}
    {data && <><TodayTaskList tasks={data.tasks} call={call} onOpen={openTask}
      onCall={(venue, action) => setCallIntent({ venue, action })} onVisit={startVisit} onSkip={skip} />
      {!!data.notifications?.length && <section className="today-notices" data-testid="today-notices">
        <h2 className="section-label">AVVISI ALLA SQUADRA</h2>{data.notifications.map((item) =>
          <button type="button" className="today-notice" key={item.id} onClick={() => setVenueId(item.venue_id)}
            data-testid={`today-notice-${item.id}`}>{item.text} ›</button>)}</section>}
      {!commercial && <TeamTodayStrip revision={revision} onOpen={(id) => window.dispatchEvent(
        new CustomEvent("luchador-open-team-person", { detail: { id } }))} />}
    </>}
    <CallConfirmSheet selection={callIntent} session={session} onClose={() => setCallIntent(null)}
      onStarted={() => { setCallIntent(null); reload(); }}
      onReturned={(record) => setCallOutcome({ ...callIntent, callId: record.id })} />
    <CallOutcomeSheet venue={callOutcome?.venue} callId={callOutcome?.callId} callAction={callOutcome?.action}
      session={session} onClose={() => setCallOutcome(null)}
      onSaved={() => { setCallOutcome(null); setNotice("Chiamata registrata."); reload(); }}
      onBook={() => startBooking(callOutcome.venue.id, null, null)} />
    <BookingSheet selection={booking} session={session} onClose={() => setBooking(null)}
      onBooked={() => { setNotice("Incontro fissato. Verifica lo stato dell'invito Google nella scheda locale."); reload(); }}
      onAgenda={() => { setBooking(null); navigate("/agenda"); }} />
    <OutcomeSheet appointmentId={outcomeId} session={session} onClose={() => setOutcomeId(null)}
      onSaved={() => { setNotice("Esito registrato."); reload(); }}
      onPositive={(result) => window.setTimeout(() => setBooking({ venueId: result.venue_id,
        participantIds: result.participant_ids }), 180)}
      onMove={(result) => window.setTimeout(() => setRescheduleId(result.id), 180)} />
    <RescheduleSheet appointmentId={rescheduleId} session={session} onClose={() => setRescheduleId(null)}
      onSaved={reload} onAgenda={() => { setRescheduleId(null); navigate("/agenda"); }} />
    <VenueSheet venueId={venueId} session={session} bookingMode={false} onClose={() => setVenueId(null)} onChanged={reload} />
  </div>;
}